import { motion } from 'motion/react';
import { Download, Scan, Sparkles, CheckCircle2, ArrowDown } from 'lucide-react';

const steps = [
  {
    icon: Download,
    step: '01',
    title: 'Descarga la app',
    description: 'Descarga Frappi desde Google Play y crea tu cuenta en segundos.',
    color: 'from-blue-500 to-cyan-500',
    lightBg: 'bg-blue-50',
    iconColor: 'text-blue-500',
  },
  {
    icon: Scan,
    step: '02',
    title: 'Escanea tus tareas',
    description: 'Toma una foto de tu syllabus, horario o tarea. La IA extrae toda la información.',
    color: 'from-violet-500 to-purple-600',
    lightBg: 'bg-violet-50',
    iconColor: 'text-violet-500',
  },
  {
    icon: Sparkles,
    step: '03',
    title: 'Genera tu plan',
    description: 'La IA crea un plan personalizado basado en tus tareas y tiempo disponible.',
    color: 'from-amber-500 to-orange-500',
    lightBg: 'bg-amber-50',
    iconColor: 'text-amber-500',
  },
  {
    icon: CheckCircle2,
    step: '04',
    title: 'Alcanza tus metas',
    description: 'Completa tareas a tiempo, mejora calificaciones y reduce el estrés.',
    color: 'from-green-500 to-emerald-500',
    lightBg: 'bg-green-50',
    iconColor: 'text-green-500',
  },
];

export function HowItWorks() {
  return (
    <section id="how-it-works" className="py-24 md:py-36 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/30 to-background" />

      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center mb-20"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-primary/20 text-primary text-sm font-semibold mb-6 shadow-sm"
          >
            Cómo funciona
          </motion.span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-foreground mb-6 tracking-tight">
            Comienza en{' '}
            <span className="bg-gradient-to-r from-[#a67eec] via-[#5bb4f2] to-[#5ddba3] bg-clip-text text-transparent">
              4 simples pasos
            </span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            Nuestra IA hace el trabajo pesado para que tú puedas enfocarte en lo que importa: aprender.
          </p>
        </motion.div>

        {/* Steps - Desktop horizontal */}
        <div className="hidden lg:block max-w-6xl mx-auto">
          <div className="grid grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.12 }}
                className="relative"
              >
                {/* Connector */}
                {index < steps.length - 1 && (
                  <div className="absolute top-16 left-[65%] w-[70%] flex items-center">
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 + index * 0.15, duration: 0.5 }}
                      className={`w-full h-0.5 bg-gradient-to-r ${step.color} opacity-30 origin-left`}
                    />
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 0.4 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.8 + index * 0.15 }}
                    >
                      <ArrowDown size={14} className={`${step.iconColor} -ml-1 rotate-[-90deg]`} />
                    </motion.div>
                  </div>
                )}

                <motion.div
                  whileHover={{ y: -8 }}
                  className="relative text-center group"
                >
                  {/* Step icon with ring */}
                  <div className="relative inline-block mb-8">
                    {/* Animated ring */}
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                      className={`absolute -inset-3 rounded-full border-2 border-dashed opacity-20`}
                      style={{ borderColor: `var(--tw-gradient-from)` }}
                    />
                    <motion.div
                      whileHover={{ scale: 1.08, rotate: 5 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                      className={`relative w-28 h-28 rounded-3xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-xl group-hover:shadow-2xl transition-shadow duration-300`}
                    >
                      <step.icon className="w-12 h-12 text-white" />

                      {/* Step number badge */}
                      <span className="absolute -top-2 -right-2 w-9 h-9 rounded-xl bg-white border-2 border-border/50 flex items-center justify-center text-sm font-extrabold text-foreground shadow-lg">
                        {step.step}
                      </span>
                    </motion.div>
                  </div>

                  <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed max-w-[250px] mx-auto">
                    {step.description}
                  </p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Steps - Mobile vertical timeline */}
        <div className="lg:hidden max-w-lg mx-auto">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="relative flex gap-5 pb-10 last:pb-0"
            >
              {/* Vertical line */}
              {index < steps.length - 1 && (
                <div className="absolute left-[30px] top-[72px] w-0.5 h-[calc(100%-72px)]">
                  <motion.div
                    initial={{ scaleY: 0 }}
                    whileInView={{ scaleY: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + index * 0.1, duration: 0.4 }}
                    className={`w-full h-full bg-gradient-to-b ${step.color} opacity-20 origin-top`}
                  />
                </div>
              )}

              {/* Icon */}
              <div className="shrink-0">
                <div className={`relative w-[60px] h-[60px] rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}>
                  <step.icon className="w-7 h-7 text-white" />
                  <span className="absolute -top-1.5 -right-1.5 w-7 h-7 rounded-lg bg-white border border-border/50 flex items-center justify-center text-xs font-extrabold text-foreground shadow">
                    {step.step}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="pt-1">
                <h3 className="text-lg font-bold text-foreground mb-2">{step.title}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
